import type { SearchState, SortOrder } from '../models/SearchModels';

export const defaultSearchState: SearchState = {
  query: '',
  division: 'all',
  page: 1,
  sortOrder: 'r',
  facetSelected: '',
  categorySk: 0,
  disciplineSk: 0,
  seriesSk: 0,
  disciplineType: '',
  showFacet: '',
  resultType: 'P'
};

function numberValue(value: string | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function decodeSegment(value: string | undefined): string {
  if (!value) return '';
  try { return decodeURIComponent(value).replaceAll('158H', ':'); }
  catch { return value.replaceAll('158H', ':'); }
}

function encodeSegment(value: string): string {
  return encodeURIComponent(value.replaceAll(':', '158H')).replaceAll('%2F', '/');
}

export function parseSearchUrl(pathname?: string, queryString?: string): SearchState {
  const resolvedPathname = pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  const resolvedQueryString = queryString ?? (typeof window === 'undefined' ? '' : window.location.search);
  const segments = resolvedPathname.replace(/^\/+|\/+$/g, '').split('/');
  const searchIndex = segments.findIndex((segment) => segment.toLowerCase() === 'search');
  const values: Record<string, string> = {};
  const start = searchIndex >= 0 ? searchIndex + 1 : 0;

  new URLSearchParams(resolvedQueryString).forEach((value, key) => {
    values[key.toLowerCase()] = value;
  });
  for (let index = start; index < segments.length - 1; index += 2) {
    values[segments[index].toLowerCase()] = segments[index + 1];
  }

  const sort = (values.sortorder ?? defaultSearchState.sortOrder).toLowerCase();
  return {
    ...defaultSearchState,
    page: numberValue(values.pg ?? values.page) || 1,
    sortOrder: (['r', 'a', 'd', 'l'].includes(sort) ? sort : 'r') as SortOrder,
    division: decodeSegment(values.div ?? values.division) || 'all',
    query: decodeSegment(values.qry ?? values.q),
    facetSelected: decodeSegment(values.f),
    categorySk: numberValue(values.cd),
    disciplineSk: numberValue(values.d),
    seriesSk: numberValue(values.s),
    disciplineType: decodeSegment(values.dt),
    showFacet: decodeSegment(values.sl)
  };
}

export function buildSearchUrl(state: SearchState): string {
  const segments: Array<string | number> = ['search', 'pg', state.page, 'sortOrder', state.sortOrder, 'div', state.division];
  if (state.facetSelected) segments.push('f', state.facetSelected);
  if (state.categorySk) segments.push('cd', state.categorySk);
  if (state.disciplineSk) segments.push('d', state.disciplineSk);
  if (state.seriesSk) segments.push('s', state.seriesSk);
  if (state.disciplineType) segments.push('dt', state.disciplineType);
  if (state.showFacet) segments.push('sl', state.showFacet);
  segments.push('qry', encodeSegment(state.query));
  return `/${segments.join('/')}`;
}

export function updateSearchUrl(state: SearchState, replace = false): void {
  window.history[replace ? 'replaceState' : 'pushState']({}, '', buildSearchUrl(state));
}
